import { HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { NotificationPreference } from '../../models/entities/notification-preference';
import { User } from '../../models/entities/user';
import type {
    AuthenticationResponseJSON,
    PublicKeyCredentialCreationOptionsJSON,
    PublicKeyCredentialRequestOptionsJSON,
    RegistrationResponseJSON,
} from '@simplewebauthn/browser';

export interface TwoFactorVerificationResponse
{
    verificationKey: string;

    /**
     * Recovery-code redemption only (SD-11060): the code minted to replace the one just spent, in plain
     * text, to be shown to the user exactly once. Null when the server issued none: the code was redeemed
     * to regenerate the set, or to disable the user's last factor, both of which discard every code, or
     * with no purpose at all.
     */
    replacementRecoveryCode?: string | null;
}

/**
 * What a recovery code is being redeemed for (SD-11060). Mirrors core's `RecoveryCodePurpose`. The SERVER
 * decides whether to mint a replacement (`AuthController::shouldIssueReplacementRecoveryCode`): it skips one
 * for `regenerate` (the whole set is replaced), and for `disable` only when the user has a single factor left
 * (removing it deletes every code). Disabling one of two factors still gets a replacement. With no purpose
 * the server mints nothing (that is the mobile app, which cannot show one yet), so every web caller states
 * one: login and payment login send `login`, password reset sends `password_reset`.
 */
export enum RecoveryCodePurpose
{
    LOGIN = 'login',
    PASSWORD_RESET = 'password_reset',
    DISABLE = 'disable',
    REGENERATE = 'regenerate',
}

/**
 * A passkey as Settings lists it (SD-9396). Addressed by `id` only: the server never sends the
 * credential id or public key. `isSynced` is the authenticator's backup state (a synced passkey
 * follows the user across devices).
 */
export interface Passkey
{
    id: number;
    name: string;
    createdAt: string;
    lastUsedAt: string | null;
    backupEligible: boolean;
    isSynced: boolean;
}

/** A fresh second-factor proof for a sensitive change, as the recovery-code regenerate endpoint takes. */
export interface PasskeyStepUp
{
    twoFactorType: string;
    twoFactorKey: string;
}

/**
 * Response shape for endpoints that issue one-time 2FA recovery codes — the enrollment
 * endpoints (google/setup, sms/confirm) and the regenerate endpoint (SD-8082). The plaintext
 * codes are returned exactly once, to be shown to the user and never persisted. May be an
 * empty array when the feature is off for the org or an active set already exists.
 */
export interface RecoveryCodesResponse
{
    recoveryCodes: string[];
}

export interface TrustedDevice
{
    id: number;
    browserLabel: string;
    ipAddress: string;
    createdAt: string;
    expiresAt: string;
}

export class PasswordChangeRequest
{
    oldPassword: string;
    newPassword: string;

    constructor(request: any) {
        if (request) {
            if (request.oldPassword) {
                this.oldPassword = request.oldPassword;
            }
            if (request.newPassword) {
                this.newPassword = request.newPassword;
            }
        }
    }
}

export interface GoogleQRResponse
{
    qr: string;
    secret: string;
}

export interface AlwaysSentNotification {
    name: string;
    reason: string;
}

export interface NotificationPreferencesResponse {
    preferences: NotificationPreference[];
    alwaysSent?: AlwaysSentNotification[];
}

@Injectable({
    providedIn: 'root',
})
export class UserSettingsService
{
    private _http: ApplicationHttpClient;

    constructor(_http: ApplicationHttpClient) {
        this._http = _http;
    }

    /**
     * SD-10091: your own user record, carrying your 2FA enrolment (core appends it for self reads only).
     * Enrolment is read here rather than from the member record because this is the one read the super 2FA
     * gate leaves open to an un-enrolled super (and it needs no memberId on the JWT).
     */
    public getOwnUser(): Observable<User> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.User(jwt.id))).pipe(
                map((response) => new User(response)),
        );
    }

    public getGoogleAuthQrCode(): Observable<GoogleQRResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserGoogleAuthSetup(jwt.id)));
    }

    public finishGoogleAuthSetup(currentCode, secretKey): Observable<RecoveryCodesResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserGoogleAuthSetup(jwt.id)), JSON.stringify({
            code: currentCode,
            secret: secretKey,
        }));
    }

    public removeGoogleAuth(code: string, userId: number = null) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserGoogleAuthDisable(userId || jwt.id)), JSON.stringify({
            code: code,
        }));
    }

    public removeSmsAuth(code: string, userId: number = null) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserSmsAuthDisable(userId || jwt.id)), JSON.stringify({
            code: code,
        }));
    }

    public startSmsAuth(phoneNumber: string): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserSmsAuthSetup(jwt.id)),
                JSON.stringify({
                    phone: phoneNumber,
                }));
    }

    public finishSmsSetup(currentCode, phoneNumber): Observable<RecoveryCodesResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserSmsAuthConfirm(jwt.id)), JSON.stringify({
            code: currentCode,
            phoneNumber: phoneNumber,
        }));
    }

    public sendSmsVerificationCode(userId: number = null): Observable<Object> {
        if (!userId) {
            const jwt: JwtLegFiClaims = LegFiJwtService.read();
            if (jwt === null) {
                return this._http.redirectAndThrow401Observable();
            }
            userId = jwt.id;
        }

        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserSmsAuthSend(userId)), JSON.stringify({}));
    }

    public verifySmsCode(currentCode, userId = null, trustDevice = false): Observable<TwoFactorVerificationResponse> {
        if (!userId) {
            const jwt: JwtLegFiClaims = LegFiJwtService.read();
            if (jwt === null) {
                return this._http.redirectAndThrow401Observable();
            }
            userId = jwt.id;
        }

        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserSmsAuthValidate(userId)), JSON.stringify({
            code: currentCode,
            trustDevice,
        }));
    }

    public verifyGoogleAuthCode(currentCode, userId = null, trustDevice = false): Observable<TwoFactorVerificationResponse> {
        if (!userId) {
            const jwt: JwtLegFiClaims = LegFiJwtService.read();
            if (jwt === null) {
                return this._http.redirectAndThrow401Observable();
            }
            userId = jwt.id;
        }

        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserGoogleAuthValidate(userId)), JSON.stringify({
            code: currentCode,
            trustDevice,
        }));
    }

    /**
     * Redeem a one-time 2FA recovery (backup) code (SD-8082). Public/pre-login, mirroring the
     * google/sms validate endpoints: on success the server mints a recovery-type verification
     * key the caller replays to the login or reset gate (as twoFactorType 'recovery').
     */
    public validateRecoveryCode(
            currentCode,
            userId = null,
            trustDevice = false,
            purpose: RecoveryCodePurpose | null = null,
    ): Observable<TwoFactorVerificationResponse> {
        if (!userId) {
            const jwt: JwtLegFiClaims = LegFiJwtService.read();
            if (jwt === null) {
                return this._http.redirectAndThrow401Observable();
            }
            userId = jwt.id;
        }

        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserRecoveryValidate(userId)), JSON.stringify({
            code: currentCode,
            trustDevice,
            ...(purpose ? {purpose} : {}),
        }));
    }

    /**
     * Regenerate the user's 2FA recovery codes (SD-8082), invalidating the previous set. Requires
     * a fresh 2FA re-authentication — the caller passes a recent verification key/type obtained
     * from one of the validate endpoints. Returns the new plaintext codes to show exactly once.
     */
    public regenerateRecoveryCodes(twoFactorType: string, twoFactorKey: string, userId: number = null): Observable<RecoveryCodesResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserRecoveryRegenerate(userId || jwt.id)), JSON.stringify({
            twoFactorType,
            twoFactorKey,
        }));
    }

    /** SD-9396: the signed-in user's active passkeys. */
    public getPasskeys(): Observable<Passkey[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserPasskeys(jwt.id)));
    }

    /** SD-9396: start adding a passkey. Spends the step-up key; returns the browser's creation options. */
    public getPasskeyRegistrationOptions(stepUp: PasskeyStepUp): Observable<PublicKeyCredentialCreationOptionsJSON> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post<{options: PublicKeyCredentialCreationOptionsJSON}>(
                Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserPasskeyRegistrationOptions(jwt.id)),
                JSON.stringify(stepUp),
        ).pipe(map((response) => response.options));
    }

    public registerPasskey(credential: RegistrationResponseJSON, name: string): Observable<Passkey> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserPasskeys(jwt.id)), JSON.stringify({credential, name}));
    }

    public renamePasskey(passkeyId: number, name: string): Observable<Passkey> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.patch(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserPasskey(jwt.id, passkeyId)), JSON.stringify({name}));
    }

    public revokePasskey(passkeyId: number, stepUp: PasskeyStepUp): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        // The ContentTypeInterceptor leaves DELETE without a Content-Type, so without this header core never
        // parses the body and the step-up key is lost (SD-9396).
        return this._http.delete(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserPasskey(jwt.id, passkeyId)), {
            body: JSON.stringify(stepUp),
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
        });
    }

    /**
     * SD-9396: pre-login. Only answers for the user whose password THIS browser session just
     * verified, so it is called straight after a /login that returned a `twoFactor` challenge.
     */
    public getPasskeyLoginOptions(userId: number): Observable<PublicKeyCredentialRequestOptionsJSON> {
        return this._http.post<{options: PublicKeyCredentialRequestOptionsJSON}>(
                Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserPasskeyLoginOptions(userId)),
                JSON.stringify({}),
        ).pipe(map((response) => response.options));
    }

    /** SD-9396: pre-login. Returns the key replayed into /login as `twoFactorType: 'passkey'`. */
    public verifyPasskeyLogin(userId: number, credential: AuthenticationResponseJSON, trustDevice = false): Observable<TwoFactorVerificationResponse> {
        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserPasskeyLoginValidate(userId)), JSON.stringify({
            credential,
            trustDevice,
        }));
    }

    public getTrustedDevices(userId: number = null): Observable<TrustedDevice[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserTrustedDevices(userId || jwt.id)));
    }

    public revokeTrustedDevice(tokenId: number, userId: number = null): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.delete(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserTrustedDevice(userId || jwt.id, tokenId)));
    }

    public revokeAllTrustedDevices(userId: number = null): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.delete(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserTrustedDevices(userId || jwt.id)));
    }

    /**
     * Gets user details from Core.
     * @param {number} userId
     * @returns {Observable}
     */
    public getUser(userId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(
                Routes.MakeLegFiCoreUrl(Routes.LegFiCore.User(userId || jwt.id)),
        );
    }

    /**
     * Updates user details in Core.
     * @param {number} userId
     * @param {Object} requestBody
     * @returns {Observable}
     */
    public patchUser(
            userId: number,
            requestBody: Object,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.patch(
                Routes.MakeLegFiCoreUrl(Routes.LegFiCore.User(userId || jwt.id)),
                JSON.stringify(requestBody),
        );
    }

    /**
     * Gets user + membership details from Core.
     * @param {number} userId
     * @param {Object} requestBody
     * @returns {Observable}
     */
    public changePassword(
            userId: number,
            requestBody: PasswordChangeRequest,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.UserChangePassword(userId || jwt.id),
                ),
                JSON.stringify(requestBody),
        );
    }

    /**
     * Gets a user's notification preferences.
     * @param userId
     * @returns {Observable<NotificationPreference[]>}
     */
    public getNotificationPreferences(userId: number): Observable<NotificationPreference[] | NotificationPreferencesResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.UserNotificationPreferences(userId || jwt.id),
                ),
        );
    }

    /**
     * Update a user's notification preferences.
     * @param userId
     * @param preferenceId
     * @param updateRequest
     * @returns {Observable<NotificationPreference>}
     */
    public patchNotificationPreferences(
            userId: number,
            preferenceId: number,
            updateRequest: Object,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.patch(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.UserNotificationPreference(
                                userId || jwt.id,
                                preferenceId,
                        ),
                ),
                JSON.stringify(updateRequest),
        ).pipe(
                map((response: Object) => {
                    return new NotificationPreference(response);
                }));
    }


}
